import _ from 'underscore';

import { dismissDialogReasons } from '../../app/actions/RequestFailedAction';
import { Module } from '../Module';
import { IModalInstance, IModalOptions, IModalPositionOptions, IModalScope, ModalService } from './ModalService';

export interface IConfirmActionDialogButton {
  label: string;
  testId?: string;    // Used for tb-test-id attribute. Defaults to 'confirm-action-dialog-confirm'.
  isDeleteAction?: boolean; // Used to toggle on red button styling
  action: (confirmActionDialogInstance: IConfirmActionDialogInstance) => any;
  disabled?: () => boolean;
}

export interface IConfirmActionDialogOptions {
  title: string;
  bodyText?: string;
  bodyTemplateUrl?: string;
  scope?: ng.IScope;
  position?: IModalPositionOptions;
  size?: string;
  areYouReallySure?: boolean; // a second layer of confirmation for destructive actions

  // Single button
  actionName?: string;

  // Method invoked when the user presses the confirm button. The method should return a promise. Otherwise, the dialog
  // will close before the method is finished executing.
  action?: (confirmActionDialogInstance: IConfirmActionDialogInstance) => any;

  confirmDisabled?: () => boolean;
  isDeleteAction?: boolean; // Used to toggle on red button styling

  // Multiple buttons
  buttons?: IConfirmActionDialogButton[];
}

export interface IConfirmActionDialogInstance extends IModalInstance {
  setProgress: (progress: number) => void;
  showErrorMessage: (errorMessage: string) => void;
}

export interface IConfirmActionDialogScope extends IModalScope {
  config: IConfirmActionDialogOptions;

  // While set to true, the dialog will show a wait spinner. Automatically set back to false as soon as the promise
  // returned by the action delegate is kept.
  busy: boolean;

  errorMessage: string;
  initialFocus: IConfirmActionDialogButton;
  progress: {
    percent: number;
  };
  areYouReallySure?: boolean;
  confirm: () => void;
  setActor: (button: IConfirmActionDialogButton) => void;
  isActor: (button: IConfirmActionDialogButton) => boolean;
}

/**
 * @deprecated This should be used only in legacy Angular code. Use ReactActionDialog or ReactConfirmationDialog instead.
 */
export class ConfirmActionDialog {

  public static $inject = ['$rootScope', 'ModalService', '$q'];

  constructor(private $rootScope: ng.IScope, private modal: ModalService, private $q: ng.IQService) {
  }

  /**
   * @deprecated This should be used only in legacy Angular code. Use ReactActionDialog or ReactConfirmationDialog instead.
   */
  public open(config: IConfirmActionDialogOptions) {
    if(!_.isUndefined(config.bodyText) && !_.isUndefined(config.bodyTemplateUrl)) {
      throw new Error('bodyTemplateUrl cannot be defined if bodyText is defined on an IConfirmActionDialogOptions object');
    }

    // Convert single button to multiple buttons
    if (!config.buttons && config.actionName) {
      config.buttons = [
        {
          label: config.actionName,
          action: config.action,
          disabled: config.confirmDisabled
        }
      ];
    }

    let lastError: string = undefined;
    let buttonActor: IConfirmActionDialogButton;

    const confirmActionDialogScope: IConfirmActionDialogScope = <IConfirmActionDialogScope> (config.scope || this.$rootScope).$new();
    confirmActionDialogScope.areYouReallySure = config.areYouReallySure || false;

    confirmActionDialogScope.setActor = (button: IConfirmActionDialogButton) => buttonActor = button;
    confirmActionDialogScope.isActor = (button: IConfirmActionDialogButton) => buttonActor === button;
    confirmActionDialogScope.progress = { percent: undefined };

    // Determine initial focus
    confirmActionDialogScope.initialFocus = _.find(config.buttons,
      (button: IConfirmActionDialogButton) => !button.disabled || !button.disabled());

    const modalOptions: IModalOptions = {
      closeX: false,
      templateUrl: 'confirmActionDialog.html',
      position: config.position,
      scope: confirmActionDialogScope,
      controller: [
        '$scope', (dialogScope: IConfirmActionDialogScope) => {

          dialogScope.config = config;
          dialogScope.busy = false;

          dialogScope.confirm = () => {
            if (!buttonActor) {
              return;
            }

            dialogScope.busy = true;
            dialogScope.errorMessage = undefined;
            lastError = undefined;
            dialogScope.progress.percent = undefined;

            // prevent closing of the dialog while we are waiting for the actionPromise to resolve
            modalOptions.keyboard = false;
            modalOptions.backdrop = 'static'; // static backdrops don't close the modal on click.

            // Convert the action result to a promise, if necessary
            const actionPromise = this.$q.when(buttonActor.action(confirmActionDialogInstance));

            actionPromise.then((result: any) => {
              if (lastError) {
                dialogScope.errorMessage = lastError;
              } else {
                dialogScope.close(result);
              }
            })
              .finally(() => {
                dialogScope.busy = false;
                modalOptions.backdrop = true;
                modalOptions.keyboard = true;
              });
          };
        }
      ],
      size: config.size
    };

    // Pre-load the dialog body template to avoid flickering/re-layout
    if (config.bodyTemplateUrl) {
      modalOptions.resolve = {
        bodyTemplateContent: ['$http', '$templateCache', ($http: ng.IHttpService, $templateCache: ng.ITemplateCacheService) => {
          return $http.get(config.bodyTemplateUrl, { cache: $templateCache });
        }]
      };
    }

    const confirmActionDialogInstance = <IConfirmActionDialogInstance> this.modal.open(modalOptions);

    confirmActionDialogInstance.showErrorMessage = (error: string) => {
      lastError = error;
    };

    confirmActionDialogInstance.setProgress = (progress: number) => {
      confirmActionDialogScope.progress.percent = progress;
    };

    confirmActionDialogInstance.result
    .finally(() => confirmActionDialogScope.$destroy())
    .catch(function(err) {
      if(!dismissDialogReasons.includes(err)) {
        throw err;
      }
    });
    return confirmActionDialogInstance;
  }

}

Module.instance().service('ConfirmActionDialog', ConfirmActionDialog);
